import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Building2, Users2, MapPin, LayoutGrid, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-3xl bg-white shadow-card p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
      </div>
      <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{value ?? "—"}</div>
    </div>
  );
}

function LinkCard({ to, title, description, testId }) {
  return (
    <Link to={to} data-testid={testId} className="rounded-3xl bg-white shadow-card p-6 hover:shadow-card-hover transition hover:-translate-y-0.5 flex items-start gap-3">
      <div className="flex-1">
        <div className="font-heading text-ink-900 text-lg">{title}</div>
        <div className="text-sm text-ink-500 mt-0.5">{description}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-ink-500" />
    </Link>
  );
}

export default function PlatformHome() {
  const t = useT();
  const orgs = useQuery({
    queryKey: ["platform-orgs"],
    queryFn: async () => (await api.get("/platform/organizations")).data,
    retry: false,
  });
  const analytics = useQuery({
    queryKey: ["platform-analytics-org"],
    queryFn: async () => (await api.get("/platform/analytics/org")).data,
    retry: false,
  });

  const orgList = Array.isArray(orgs.data) ? orgs.data : orgs.data?.organizations || orgs.data?.items || [];
  const primaryOrg = orgList[0];
  const overview = analytics.data || {};

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t("nav.business")}
        title={t("platform.title")}
        subtitle={t("platform.subtitle")}
      />

      {orgs.isLoading && <LoadingCard height="h-24" />}
      {orgs.isError && <ErrorState message={errMsg(orgs.error)} onRetry={() => orgs.refetch()} />}

      {primaryOrg && (
        <div className="rounded-3xl bg-gradient-to-br from-cream-100 to-white shadow-card p-6 md:p-8 flex items-start gap-6">
          <div className="h-14 w-14 rounded-2xl bg-accents-business flex items-center justify-center shrink-0">
            <Building2 className="h-6 w-6 text-white" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">{t("platform.orgOverview")}</div>
            <div className="font-heading text-2xl text-ink-900 mt-1 truncate">{primaryOrg.name || primaryOrg.title || "—"}</div>
            <div className="text-sm text-ink-500 mt-1">{primaryOrg.plan || primaryOrg.tier || ""}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Stat testId="pf-stat-members" icon={Users2} label={t("nav.people")} value={overview.members ?? overview.people ?? "—"} />
        <Stat testId="pf-stat-teams" icon={Users2} label={t("nav.teams")} value={overview.teams ?? "—"} />
        <Stat testId="pf-stat-locations" icon={MapPin} label={t("nav.locations")} value={overview.locations ?? "—"} />
        <Stat testId="pf-stat-departments" icon={LayoutGrid} label={t("nav.departments")} value={overview.departments ?? "—"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <LinkCard testId="pf-link-people" to="/app/business/people" title={t("nav.people")} description="Manage members, roles and audit." />
        <LinkCard testId="pf-link-teams" to="/app/business/teams" title={t("nav.teams")} description="Teams, leaders, hierarchy." />
        <LinkCard testId="pf-link-invites" to="/app/business/invites" title={t("nav.invites")} description={t("platform.outbox")} />
        <LinkCard testId="pf-link-analytics" to="/app/business/analytics" title={t("nav.analytics")} description="Org & team analytics." />
        <LinkCard testId="pf-link-wellbeing" to="/app/business/wellbeing" title={t("nav.wellbeing")} description={t("platform.wellbeingAggregate")} />
        <LinkCard testId="pf-link-audit" to="/app/business/audit" title={t("nav.audit")} description={t("platform.audit")} />
      </div>
    </div>
  );
}
